/** The Pre-check-in tab's draft widgets: the shared pre-check pieces (`features/precheck/
 *  editor`) over the operator's GET (`useOperatorPrecheck`) and the draft — the operator
 *  tab's counterpart of the tour's `PreCheckBlock` adapters.
 *
 *  - Field lists show a custom field with its definition's label and type; renaming a row
 *    renames the field wherever it is asked (a stored custom field's definition, else every
 *    row with its id in both lists — `fieldEdit`). "Add a field" offers the operator's
 *    custom fields, the stored ones and those this draft created in the other list.
 *  - Questions say what the guest form derives for each category, and a category the
 *    operator does not have blocks Save (the server refuses the whole form for it).
 *  - Waivers in the order guests see them, with languages and audience tags; only enabled
 *    waivers can be added; a row that is not the operator's blocks Save.
 *
 *  Each control is one undo step over every path it moves; a rename's keystrokes are one
 *  step per focus session. */
import { Box, Switch, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';

import { FieldsList, type FieldsEdit } from '@/features/precheck/editor/FieldsList';
import { QuestionsList } from '@/features/precheck/editor/QuestionsList';
import { categoryCode, categoryNameOf } from '@/features/precheck/editor/sources';
import { WaiverList } from '@/features/precheck/editor/WaiverList';
import {
  APPLY_SAME_PATH,
  applySameEdit,
  asFields,
  precheckEdit,
  SCOPE_PATHS,
  scopeState,
  splitFields,
  type PreCheckField,
  type PreCheckScope,
} from '@/features/precheck/model';
import { useDraft } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import type { RecordWidget, RecordWidgetProps } from '@/features/records/editor/types';
import { CHANGED_TINT } from '@/features/records/fields/FieldSlot';

import { useOperatorPrecheck } from './context';
import {
  asDefinitions,
  asWaiverRows,
  DEFINITIONS_PATH,
  fieldEdit,
  LEAD_FIELDS,
  newCustomFields,
  overlayDefinitions,
  PARTY_FIELDS,
  WAIVERS_PATH,
} from './model';

/** The draft's value of a path (else the stored one), and one SET over several paths. */
export function useOperatorDraft(rec: string) {
  const { state, dispatch, baselineOf } = useDraft();
  const valueOf = (path: string) => {
    const e = state.entries[rec]?.[path];
    return e ? e.to : baselineOf(rec, path);
  };
  const apply = (values: Record<string, unknown>, label?: string, coalesce?: string | null) =>
    dispatch({
      type: 'SET',
      changes: Object.entries(values).map(([path, to]) => ({ rec, path, from: baselineOf(rec, path), to })),
      ...(label ? { label } : {}),
      ...(coalesce ? { coalesce } : {}),
    });
  return { valueOf, apply };
}

let renameSession = 0;

/** One focus session of a text input as one undo step. */
export function useCoalesceSession(scope: string) {
  const token = useRef<string | null>(null);
  return {
    token: () => token.current,
    begin: () => {
      renameSession += 1;
      token.current = `${scope}#${renameSession}`;
    },
    end: () => {
      token.current = null;
    },
  };
}

const scopeOf = (key: string): PreCheckScope => (key.includes('party') ? 'party' : 'lead');

/** The custom fields "Add a field" offers: the stored ones as the draft leaves them, then
 *  those the draft created (in either list). */
export function useOfferedDefinitions(valueOf: (path: string) => unknown) {
  const { storedIds } = useOperatorPrecheck();
  return [...asDefinitions(valueOf(DEFINITIONS_PATH)), ...newCustomFields([valueOf(LEAD_FIELDS), valueOf(PARTY_FIELDS)], storedIds)];
}

// ---------------------------------------------------------------------------
// Views (the stored values, on the edit widgets' geometry)
// ---------------------------------------------------------------------------

export function FieldsView({ scope }: { scope: PreCheckScope }) {
  const { baseline, categoryFacts } = useOperatorPrecheck();
  const stored = baseline.paths[baseline.rec] ?? {};
  const value = overlayDefinitions(stored[SCOPE_PATHS[scope].fields], asDefinitions(stored[DEFINITIONS_PATH]));
  return (
    <FieldsList
      scope={scope}
      value={value}
      follows={scope === 'party' && stored[APPLY_SAME_PATH] === true}
      edit={null}
      questionFacts={categoryFacts}
    />
  );
}

export function QuestionsView({ scope }: { scope: PreCheckScope }) {
  const { baseline, categories, categoryFacts, tenant } = useOperatorPrecheck();
  const stored = baseline.paths[baseline.rec] ?? {};
  const st = scopeState((p) => stored[p], scope);
  return (
    <QuestionsList
      scope={scope}
      enabled={st.enabled}
      selected={st.selected}
      follows={scope === 'party' && stored[APPLY_SAME_PATH] === true}
      invalid={null}
      edit={null}
      categories={categories}
      tenant={tenant}
      facts={categoryFacts}
    />
  );
}

export function WaiversView() {
  const { baseline, enabledWaivers, waiverDetails, tenant } = useOperatorPrecheck();
  return (
    <WaiverList
      value={baseline.paths[baseline.rec]?.[WAIVERS_PATH]}
      edit={null}
      waivers={enabledWaivers}
      tenant={tenant}
      details={waiverDetails}
    />
  );
}

// ---------------------------------------------------------------------------
// Widgets (edit mode)
// ---------------------------------------------------------------------------

function OperatorFieldsWidget(p: RecordWidgetProps) {
  const scope = scopeOf(p.field.key);
  const { valueOf, apply } = useOperatorDraft(p.rec);
  const { baselineOf } = useDraft();
  const { errorsFor } = useRecordEditor();
  const { storedIds, categories, categoryFacts } = useOperatorPrecheck();
  const nameOf = categoryNameOf(categories.list);
  const shown = overlayDefinitions(p.value, asDefinitions(valueOf(DEFINITIONS_PATH)));
  const { regular } = splitFields(shown);
  const follows = scope === 'party' && valueOf(APPLY_SAME_PATH) === true;
  const offered = useOfferedDefinitions(valueOf);
  const session = useCoalesceSession(`${p.rec}|precheck-rename`);

  // A stored custom field's row keeps its own stored label underneath (the definition's is
  // shown and sent) — so a reorder never reads as a relabel.
  const raw = new Map(asFields(p.value).map((f) => [f.id, f]));
  const own = (rows: PreCheckField[]) =>
    rows.map((f) => {
      const r = raw.get(f.id);
      return storedIds.has(f.id) && r ? { ...f, name: r.name, type: r.type } : f;
    });

  const edit: FieldsEdit = {
    disabled: p.disabled,
    onRegular: (next, label) => apply(precheckEdit(valueOf, scope, { regular: own(next) }, nameOf), label),
    onRename: (i, name) => {
      const f = regular[i];
      if (f) apply(fieldEdit(valueOf, f.id, { name }, storedIds), `Rename ${f.name}`, session.token());
    },
    onRenameFocus: session.begin,
    onRenameBlur: session.end,
  };
  // Each row says what the draft changed of it: the stored list, named as stored.
  const stored = overlayDefinitions(p.baseline, asDefinitions(baselineOf(p.rec, DEFINITIONS_PATH)));
  return (
    <FieldsList
      scope={scope}
      value={shown}
      follows={follows}
      edit={follows ? null : edit}
      definitions={offered}
      baseline={stored}
      errors={errorsFor(p.rec, p.field.key)}
      questionFacts={categoryFacts}
    />
  );
}

function OperatorQuestionsWidget(p: RecordWidgetProps) {
  const scope = scopeOf(p.field.key);
  const { valueOf, apply } = useOperatorDraft(p.rec);
  const { state } = useDraft();
  const { setInvalidInput } = useRecordEditor();
  const { categories, categoryFacts, tenant } = useOperatorPrecheck();
  const nameOf = categoryNameOf(categories.list);
  const st = scopeState(valueOf, scope);
  const follows = scope === 'party' && valueOf(APPLY_SAME_PATH) === true;
  const who = scope === 'lead' ? 'the lead booker' : 'party members';
  const paths = SCOPE_PATHS[scope];

  // Switched on with nothing to ask: Save is blocked — only when the draft made it so. A
  // question about a category the operator does not have blocks it whatever made it so:
  // the server refuses the whole form for it. Each on a key of its own (no slot shares it).
  const touched = Boolean(state.entries[p.rec]?.[paths.enabled] || state.entries[p.rec]?.[paths.selected]);
  const invalid = touched && st.enabled && st.selected.length === 0 ? 'Pick at least one category, or switch the questions off' : null;
  const gone = st.enabled ? st.selected.filter((code) => categoryFacts(code) === null) : [];
  const goneText =
    gone.length > 0
      ? `${gone.map((c) => nameOf(c) ?? c).join(', ')} ${gone.length === 1 ? 'is' : 'are'} not one of this operator’s tag categories — remove ${gone.length === 1 ? 'it' : 'them'} to save`
      : null;
  const goneKey = `${paths.selected}#unavailable`;
  useEffect(() => {
    setInvalidInput(p.rec, paths.enabled, invalid);
    setInvalidInput(p.rec, goneKey, goneText);
  }, [invalid, goneText, goneKey, p.rec, paths.enabled, setInvalidInput]);
  useEffect(
    () => () => {
      setInvalidInput(p.rec, paths.enabled, null);
      setInvalidInput(p.rec, goneKey, null);
    },
    [p.rec, paths.enabled, goneKey, setInvalidInput],
  );

  return (
    <QuestionsList
      scope={scope}
      enabled={st.enabled}
      selected={st.selected}
      follows={follows}
      invalid={invalid}
      categories={categories}
      tenant={tenant}
      facts={categoryFacts}
      edit={{
        disabled: p.disabled || follows,
        id: p.id,
        describedBy: p.describedBy,
        onEnabled: (on) =>
          apply(precheckEdit(valueOf, scope, { enabled: on }, nameOf), `${on ? 'Ask' : 'Stop asking'} ${who} tag-category questions`),
        onRemove: (code) =>
          apply(
            precheckEdit(valueOf, scope, { selected: st.selected.filter((c) => c !== code) }, nameOf),
            `Stop asking ${who} about ${nameOf(code) ?? code}`,
          ),
        onAdd: (o) => {
          const code = categoryCode(o);
          apply(
            precheckEdit(valueOf, scope, { selected: [...st.selected, code] }, (c) => (c === code ? o.name : nameOf(c))),
            `Ask ${who} about ${o.name ?? code}`,
          );
        },
      }}
    />
  );
}

function OperatorApplySameWidget(p: RecordWidgetProps) {
  const { valueOf, apply } = useOperatorDraft(p.rec);
  const { categories } = useOperatorPrecheck();
  const nameOf = categoryNameOf(categories.list);
  const on = p.value === true;
  return (
    <Box
      sx={[{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 40, borderRadius: 1, px: 0.5 }, on !== (p.baseline === true) && { bgcolor: CHANGED_TINT }]}
    >
      <Switch
        size="small"
        checked={on}
        disabled={p.disabled}
        onChange={(e) =>
          apply(
            applySameEdit(valueOf, e.target.checked, nameOf),
            e.target.checked ? 'Party members answer the lead booker’s fields' : 'Party members get their own fields',
          )
        }
        inputProps={{ id: p.id, 'aria-describedby': p.describedBy }}
      />
      <Typography variant="body2" aria-hidden>
        {on ? 'Yes' : 'No'}
      </Typography>
    </Box>
  );
}

function OperatorWaiversWidget(p: RecordWidgetProps) {
  const { enabledWaivers, waiverDetails, tenant } = useOperatorPrecheck();
  const { setInvalidInput } = useRecordEditor();
  // A row naming a waiver the operator does not have: the server refuses the whole form.
  const foreign = asWaiverRows(p.value).filter((w) => !waiverDetails(w.waiver_id)).length;
  const key = `${WAIVERS_PATH}#unavailable`;
  const text = foreign > 0 ? `${foreign} ${foreign === 1 ? 'waiver is' : 'waivers are'} not this operator’s — remove ${foreign === 1 ? 'it' : 'them'} to save` : null;
  useEffect(() => setInvalidInput(p.rec, key, text), [p.rec, key, text, setInvalidInput]);
  useEffect(() => () => setInvalidInput(p.rec, key, null), [p.rec, key, setInvalidInput]);
  return (
    <WaiverList
      value={p.value}
      edit={{ disabled: p.disabled, onChange: (next, label) => p.onChange(next, { label }) }}
      waivers={enabledWaivers}
      tenant={tenant}
      details={waiverDetails}
    />
  );
}

export const OPERATOR_PRECHECK_WIDGETS: Record<string, RecordWidget> = {
  // A list widget: its rows mark their own changes, errors and questions.
  precheck_fields: Object.assign(OperatorFieldsWidget, { owns: 'items' as const }),
  precheck_categories: OperatorQuestionsWidget,
  precheck_apply_same: OperatorApplySameWidget,
  precheck_waivers: OperatorWaiversWidget,
};
